/**
 * PreviewModeBar.tsx
 *
 * Reusable admin preview mode toggle bar.
 * Shows a "Preview as Visitor" button when in admin mode (not previewing).
 * Shows an "Exit Preview" banner when in preview mode.
 *
 * Use on any page that has admin editing capabilities.
 */

import { useAdmin } from "@/context/AdminContext";
import { useDarkMode } from "@/context/DarkModeContext";

interface PreviewModeBarProps {
  /** Optional hint text shown in the toolbar (default: "Hover sections to reorder with arrows") */
  hint?: string;
}

export default function PreviewModeBar({ hint = "Hover sections to reorder with arrows" }: PreviewModeBarProps) {
  const { isAdmin, isEditing, isPreviewMode, setPreviewMode } = useAdmin();
  const { isDark } = useDarkMode();

  if (!isAdmin) return null;

  if (isPreviewMode) {
    return (
      <div className="fixed top-0 left-0 right-0 z-[300] bg-gray-900 text-white text-xs font-semibold flex items-center justify-between px-6 py-2.5">
        <div className="flex items-center gap-2">
          <i className="ri-eye-line text-green-400"></i>
          <span>Previewing as visitor — admin controls hidden</span>
        </div>
        <button
          onClick={() => setPreviewMode(false)}
          className="flex items-center gap-1.5 bg-pink-500 hover:bg-pink-600 text-white text-xs font-bold px-3 py-1.5 rounded-full cursor-pointer whitespace-nowrap transition-colors"
        >
          <i className="ri-edit-line"></i>
          Exit Preview
        </button>
      </div>
    );
  }

  if (!isEditing) return null;

  return (
    <div className="fixed bottom-[80px] left-1/2 -translate-x-1/2 z-[150] flex items-center gap-2">
      <div className={`flex items-center gap-3 px-4 py-2.5 rounded-full text-xs font-semibold border ${isDark ? "bg-gray-800 border-gray-600 text-gray-300" : "bg-white border-gray-200 text-gray-600"}`}>
        <i className="ri-arrow-up-down-line text-pink-400"></i>
        <span>{hint}</span>
        <span className="w-px h-4 bg-gray-300"></span>
        <button
          onClick={() => setPreviewMode(true)}
          className="flex items-center gap-1.5 text-xs font-bold text-pink-500 hover:text-pink-700 cursor-pointer whitespace-nowrap transition-colors"
        >
          <i className="ri-eye-line"></i>
          Preview as Visitor
        </button>
      </div>
    </div>
  );
}